import { type RefObject, useCallback, useEffect, useRef } from 'react'
import { isEmpty } from '@terros/common'
import { calculatePosition } from './utils/positionCalculations'
import { generatePathData } from './utils/pathGenerator'
import { type ConnectionLayerOptions, type Edge, type LineType } from './types'
import { useAnimationFrame } from './hooks/useAnimationFrame'

export const useConnectionLayer = (edges: Edge[], options: ConnectionLayerOptions = {}): RefObject<SVGSVGElement> => {
  const { startPosition = 'bottom', endPosition = 'top', lineType = 'connected', lineShape = 'square' } = options
  const connectionsRef = useRef<SVGSVGElement>(null)

  const drawConnections = useCallback(() => {
    if (!connectionsRef.current) return

    while (connectionsRef.current.firstChild) {
      connectionsRef.current.removeChild(connectionsRef.current.firstChild)
    }

    const screenMatrix = connectionsRef.current.getScreenCTM()
    if (!screenMatrix) return

    if (lineShape === 'bracket') {
      // Group edges by end node
      const edgesByEndNode = edges.reduce<Record<string, Edge[]>>((acc, edge) => {
        if (!acc[edge.end]) {
          acc[edge.end] = []
        }
        acc[edge.end].push(edge)
        return acc
      }, {})

      // Process each group of edges
      Object.entries(edgesByEndNode).forEach(([endId, group]) => {
        if (isEmpty(group)) return

        const targetElement = document.querySelector(`[data-node-id="${endId}"]`)
        if (!targetElement || !connectionsRef.current) return

        const targetRect = targetElement.getBoundingClientRect()
        const targetPos = calculatePosition(
          { elementRect: targetRect, screenMatrix },
          group[0].endPosition || endPosition
        )

        // Calculate source positions
        const sourcePositions = group
          .map((edge) => {
            const sourceElement = document.querySelector(`[data-node-id="${edge.start}"]`)
            if (!sourceElement) return null

            const sourceRect = sourceElement.getBoundingClientRect()
            const edgeStartPosition = edge.startPosition || startPosition
            return {
              edge,
              pos: calculatePosition({ elementRect: sourceRect, screenMatrix }, edgeStartPosition),
              id: edge.start,
            }
          })
          .filter((s): s is { edge: Edge; pos: { x: number; y: number }; id: string } => s !== null)

        if (isEmpty(sourcePositions)) return

        // Sort source positions by y coordinate to maintain vertical order
        sourcePositions.sort((a, b) => a.pos.y - b.pos.y)

        // Create paths for each source
        sourcePositions.forEach((source, index) => {
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')

          const nodeGapX = targetPos.x - source.pos.x
          // Calculate the horizontal extension point (halfway to target)
          const horizontalExtension = source.pos.x + nodeGapX / 3

          // If this is a pair of sources (0-1, 2-3, etc), connect them
          if (index % 2 === 0 && index + 1 < sourcePositions.length) {
            const nextSource = sourcePositions[index + 1]
            const midY = (source.pos.y + nextSource.pos.y) / 2

            // Draw path: source → horizontal → vertical connection → target
            let pathData = `M ${source.pos.x} ${source.pos.y}` // Start at source
            pathData += ` L ${horizontalExtension} ${source.pos.y}` // Horizontal line
            pathData += ` L ${horizontalExtension} ${midY}` // Vertical to midpoint

            // Draw the connection to the next source
            const nextPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
            let nextPathData = `M ${nextSource.pos.x} ${nextSource.pos.y}` // Start at next source
            nextPathData += ` L ${horizontalExtension} ${nextSource.pos.y}` // Horizontal line
            nextPathData += ` L ${horizontalExtension} ${midY}` // Vertical to midpoint

            // Draw the vertical connection to target
            const verticalConnection = document.createElementNS('http://www.w3.org/2000/svg', 'path')
            let connectionData = `M ${horizontalExtension} ${midY}`
            // Extend from the connection to the target
            connectionData += ` L ${targetPos.x} ${midY}`

            setPathStyle(path, source.edge, lineType)
            setPathStyle(nextPath, nextSource.edge, lineType)
            setPathStyle(verticalConnection, source.edge, lineType)

            path.setAttribute('d', pathData)
            nextPath.setAttribute('d', nextPathData)
            verticalConnection.setAttribute('d', connectionData)

            if (connectionsRef.current) {
              connectionsRef.current.appendChild(path)
              connectionsRef.current.appendChild(nextPath)
              connectionsRef.current.appendChild(verticalConnection)
            }
          } else if (index % 2 === 0) {
            // Handle single remaining source
            let pathData = `M ${source.pos.x} ${source.pos.y}`
            pathData += ` L ${horizontalExtension} ${source.pos.y}`
            // Ensure perpendicular connection for single sources too
            pathData += ` L ${targetPos.x - nodeGapX / 3} ${source.pos.y}`
            pathData += ` L ${targetPos.x - nodeGapX / 3} ${targetPos.y}`
            pathData += ` L ${targetPos.x} ${targetPos.y}`

            path.setAttribute('d', pathData)
            setPathStyle(path, source.edge, lineType)

            if (connectionsRef.current) {
              connectionsRef.current.appendChild(path)
            }
          }
        })
      })
    } else {
      // Original curve line drawing logic
      edges.forEach((edge) => {
        const sourceElement = document.querySelector(`[data-node-id="${edge.start}"]`)
        const targetElement = document.querySelector(`[data-node-id="${edge.end}"]`)

        if (!sourceElement || !targetElement || !connectionsRef.current) return

        const sourceRect = sourceElement.getBoundingClientRect()
        const targetRect = targetElement.getBoundingClientRect()

        const sourcePos = calculatePosition(
          { elementRect: sourceRect, screenMatrix },
          edge.startPosition || startPosition
        )
        const targetPos = calculatePosition({ elementRect: targetRect, screenMatrix }, edge.endPosition || endPosition)

        const pathData = generatePathData(
          {
            sourceX: sourcePos.x,
            sourceY: sourcePos.y,
            targetX: targetPos.x,
            targetY: targetPos.y,
            startPosition: edge.startPosition || startPosition,
            endPosition: edge.endPosition || endPosition,
          },
          lineShape
        )

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', pathData)
        setPathStyle(path, edge, lineType)

        connectionsRef.current?.appendChild(path)
      })
    }
  }, [edges, lineShape, lineType, startPosition, endPosition])

  useAnimationFrame(drawConnections)

  useEffect(() => {
    const resizeObserver = new ResizeObserver(drawConnections)

    if (connectionsRef.current) {
      resizeObserver.observe(connectionsRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [drawConnections])

  return connectionsRef
}

function setPathStyle(path: SVGPathElement, edge: Edge, lineType: LineType | undefined): void {
  path.setAttribute('stroke', edge.color ?? '#666')
  path.setAttribute('stroke-width', String(edge.width ?? 2))
  path.setAttribute('stroke-linecap', 'round')
  path.setAttribute('fill', 'none')
  if (edge.opacity !== undefined) path.setAttribute('stroke-opacity', String(edge.opacity))
  if (lineType === 'dotted') path.setAttribute('stroke-dasharray', '5,5')
}
